/**
 * Responsible for toggling a field between being a password and plain text.
 * @module elements/fields/toggle-password
 */
import $ from 'jquery';
import noop from 'lodash/noop';
import assignWith from 'lodash/assignWith';
import isNil from 'lodash/isNil';
import stache from '../../lib/stache';

import { TYPES } from './form-field-behaviors';

export const NS = 'toggle-password';

export const TOGGLE_PASSWORD_CLASSNAME = 'toggle-password';

export const TOGGLE_PASSWORD_SELECTOR = `.${TOGGLE_PASSWORD_CLASSNAME}`;

const { CONTAINER, FIELD } = TYPES;

const TOGGLE_PASSWORD_DEFAULTS = {
  fieldAttrs: {},
  showText: 'show',
  hideText: 'hide',
  a11yShowText: 'show {{label}} field entry',
  a11yHideText: 'hide {{label}} field entry',
  actionClassName: 'toggle-password',
};

/**
 * Default customizer used to determine which value to use for options.
 * @param {*} objValue - Value from target
 * @param {*} srcValue - Value from source
 * @returns {*} Value for key
 */
const defaultCustomizer = (objValue, srcValue) => {
  if (!isNil(srcValue)) {
    return srcValue;
  }

  return objValue;
};

/**
 * Responsible for rendering and appending the show/hide toggling action.
 * @param {jQuery} $container - Where the toggler will be appended to
 * @param {jQuery} $formField - Form field element
 * @param {string} actionClassName - Class name applied to the toggler
 * @returns {jQuery} The toggler element
 */
const appendToggler = ($container, $formField, actionClassName) => {
  const formFieldId = $formField.attr('id');
  // start with hide so it forces it to show initially
  $container.append(`
    <a
      data-toggle-password-state="hide"
      class="${actionClassName} ${TOGGLE_PASSWORD_CLASSNAME}"
      href="#${!isNil(formFieldId) ? formFieldId : ''}">
    </a>
  `);
  return $container.find(TOGGLE_PASSWORD_SELECTOR);
};

/**
 * Responsible for updating the toggler based on the state of the toggler.
 * @param {Object} states - Map of the various toggling states
 * @param {jQuery} $formField - Form field element
 * @param {jQuery} $togglePassword - Toggle element
 * @param {Object} options - Configurable options
 */
const updateToggler = (states, $formField, $togglePassword, options = {}) => {
  const {
    templateData = {},
  } = options;
  const currentState = $togglePassword.data('togglePasswordState');

  const nextStateObj = states[currentState];
  if (!nextStateObj) {
    throw new Error(`No toggle password state found for ${currentState}`);
  }

  const {
    state: nextState,
    text: nextText,
    a11yText: nextA11yText,
    type: nextType,
  } = nextStateObj;

  $togglePassword
    .data('togglePasswordState', nextState)
    .attr('aria-label', stache(nextA11yText, templateData))
    .text(nextText);

  $formField.attr('type', nextType);
};

/**
 * Responsible for setting/wiring up toggle password functionality within a
 * form field behavior. In particular it'll allow for a show/hide toggling of a
 * password field with support around i18n and a11y.
 * @param {Object} [options={}] - Configurable options
 * @return {Function} Passed back the element belonging to the form field
 * behaviors
 */
const togglePassword = (options = {}) => (...nodes) => {
  const [{ node: container }] = nodes.filter(({ type }) => type === CONTAINER);
  const [{ node: formField }] = nodes.filter(({ type }) => type === FIELD);
  const $container = $(container);
  const $formField = $(formField);
  const label = $formField.data('label') || '';
  const {
    fieldAttrs,
    showText,
    hideText,
    a11yShowText,
    a11yHideText,
    actionClassName,
  } = assignWith({}, TOGGLE_PASSWORD_DEFAULTS, options, $container.data(), defaultCustomizer);

  $container.attr(fieldAttrs);

  if (!$container.data('togglePassword') === true) {
    return noop;
  }

  if (formField.type !== 'password') {
    throw new Error('togglePassword requires a field to be password input');
  }

  const states = {
    show: {
      state: 'hide',
      text: hideText,
      a11yText: a11yHideText,
      type: 'text',
    },
    hide: {
      state: 'show',
      text: showText,
      a11yText: a11yShowText,
      type: 'password',
    },
  };

  const $togglePassword = appendToggler($container, $formField, actionClassName);
  updateToggler(states, $formField, $togglePassword, {
    templateData: { label },
  });

  $togglePassword
    .on(`click.${NS}`, (e) => {
      e.preventDefault();
      updateToggler(states, $formField, $togglePassword, {
        templateData: { label },
      });
    });

  return noop;
};

export default togglePassword;



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/toggle-password.js